import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { UserCog, Beaker } from "lucide-react";
import { toast } from "sonner";

export default function RoleSelector() {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);

  const open = !!user && !user.account_role;

  const choose = async (role) => {
    setBusy(true);
    try {
      await api.post("/users/me/role", { account_role: role });
      setUser({ ...user, account_role: role });
      toast.success(`Welcome, ${role === "supervisor" ? "Supervisor" : "Lab Member"}`);
    } catch {
      toast.error("Failed to set role");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open}>
      <DialogContent className="sm:max-w-md" data-testid="role-selector-modal">
        <DialogHeader>
          <DialogTitle>How will you use ResyNote?</DialogTitle>
          <DialogDescription>
            Pick the role that fits you best. You can collaborate with the other side either way —
            this just sets up your default workspace.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 pt-2">
          <button
            onClick={() => choose("supervisor")}
            disabled={busy}
            className="border border-zinc-200 dark:border-zinc-800 rounded-md p-5 text-left hover:border-blue-500 transition-colors group"
            data-testid="role-supervisor-btn"
          >
            <UserCog className="h-6 w-6 text-blue-600 mb-3" />
            <div className="font-sans font-semibold">Supervisor</div>
            <div className="text-xs font-body text-zinc-500 mt-1.5 leading-relaxed">
              Lead a research group. Create labs, add members, monitor experiments and schedule meetings.
            </div>
          </button>
          <button
            onClick={() => choose("member")}
            disabled={busy}
            className="border border-zinc-200 dark:border-zinc-800 rounded-md p-5 text-left hover:border-blue-500 transition-colors group"
            data-testid="role-member-btn"
          >
            <Beaker className="h-6 w-6 text-blue-600 mb-3" />
            <div className="font-sans font-semibold">Lab Member</div>
            <div className="text-xs font-body text-zinc-500 mt-1.5 leading-relaxed">
              Run your own experiments, log observations and results, and join labs invited by your supervisor.
            </div>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
